import { Card, Stack, LoadingOverlay, Container } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { masterGroupService } from 'services/lia/masterGroupService';
import { MasterGroup } from 'services/api';
import PageTitle from 'components/PageTitle';
import Toolbar from 'components/Toolbar';
import DataTables, { DataTableColumn } from 'components/DataTables';
import dayjs from 'dayjs';
import { pathnames } from 'routes/lia';
const MasterQuestionPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [sortBy, setSortBy] = useState('DateIn');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const defaultSearch = new URLSearchParams(location.search).get('search') || '';

  // Keep track of previous search to detect changes
  const [previousSearch, setPreviousSearch] = useState(defaultSearch);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (defaultSearch !== previousSearch) {
      setCurrentPage(1);
      setPreviousSearch(defaultSearch);
    }
  }, [defaultSearch, previousSearch]);

  // Updated query to pass filters in correct format
  const {
    data: groupsData,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['masterGroupQuestions', defaultSearch, currentPage, pageSize, sortBy, sortOrder],
    queryFn: () =>
      masterGroupService.getList({
        search: defaultSearch,
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder
      }),
    refetchOnWindowFocus: false
  });

  const groups: any[] = groupsData?.data || [];

  const tableData = {
    data: groups.map((g) => ({
      ...g,
      numberOfQuestions: g.questions?.length ?? 0
    })),
    totalRecords: groupsData?.totalRecords || 0
  };

  const [columns, setColumns] = useState<DataTableColumn[]>([
    {
      heading: 'Group Question Name',
      props: 'title',
      sortable: true,
      active: true,
      id: 'title'
    },
    {
      heading: 'Number of Questions',
      props: 'numberOfQuestions',
      sortable: true,
      active: true,
      id: 'numberOfQuestions'
    },
    {
      heading: 'Created By',
      props: 'userIn',
      sortable: true,
      active: true,
      id: 'userIn'
    },
    {
      heading: 'Created Date',
      props: (row: MasterGroup) => dayjs(row.dateIn).format('DD MMM YYYY HH:mm'),
      sortable: true,
      active: true,
      id: 'dateIn'
    },
    {
      heading: 'Updated By',
      props: (row: MasterGroup) => row.userUp || '-',
      sortable: true,
      active: true,
      id: 'userUp'
    },
    {
      heading: 'Updated Date',
      props: (row: MasterGroup) =>
        row.dateUp ? dayjs(row.dateUp).format('DD MMM YYYY HH:mm') : '-',
      sortable: true,
      active: true,
      id: 'dateUp'
    },
    {
      heading: 'Action',
      props: 'action',
      active: true,
      id: 'action'
    }
  ]);

  // Pagination handlers
  const handleViewGroup = (row: MasterGroup) => {
    navigate(`${pathnames.questionsList}?group=${row.msGroupQuestionID}`);
  };

  return (
    <Container fluid>
      <PageTitle
        title='Master Question'
        description='Create and manage assessment questions. Easily add, edit, or remove questions to build comprehensive question banks for your evaluations.'
      />

      <Card shadow='xs' radius='md' mt={20} pos='relative'>
        <LoadingOverlay visible={isLoading || isFetching} />

        <Toolbar
          columns={columns}
          showSearch
          showCreateButton={false}
          showFilter={false}
          searchPlaceholder='Search group questions...'
          defaultSearchValue={defaultSearch}
          setColumns={setColumns}
        />

        <Stack mt={20}>
          <DataTables
            columns={columns}
            data={tableData.data}
            loading={isLoading}
            isNumbering
            activeSize={pageSize}
            sizing={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 }
            ]}
            withPagination
            totalData={tableData.totalRecords}
            currentPage={currentPage}
            currentSize={pageSize}
            handleChangePage={setCurrentPage}
            handleChangeSize={(size: number) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            withBulkData={false}
            handleView={handleViewGroup}
            viewText='View Questions'
            onSortChange={(newSortBy, newSortOrder) => {
              setSortBy(newSortBy);
              setSortOrder(newSortOrder);
              setCurrentPage(1);
            }}
            emptyMessageTitle='No group questions found'
            emptyMessageDescription='Create your group question first from the Group Question menu'
          />
        </Stack>
      </Card>
    </Container>
  );
};

export default MasterQuestionPage;
